import React from 'react';
import styled from 'styled-components';
import MyLocationIcon from '@mui/icons-material/MyLocation';

// Soft pre-prompt shown once after login (re-asked after ~30 days if dismissed).
// It does NOT call the browser geolocation API directly — only when the user
// clicks "Permitir" do we trigger the real prompt (tied to a user gesture, so the
// browser doesn't silence it and the grant rate stays high).

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 4000;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;

const Card = styled.div<{ $dark: boolean }>`
  background: ${p => (p.$dark ? '#1e1e1f' : '#ffffff')};
  color: ${p => (p.$dark ? '#ededed' : '#1a1a1a')};
  border-radius: 16px;
  max-width: 420px;
  width: 100%;
  padding: 28px 26px 22px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  text-align: center;
`;

const IconCircle = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: ${p => p.$color}22;
  color: ${p => p.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
`;

const Title = styled.h3`
  margin: 0 0 8px;
  font-size: 1.15rem;
  font-weight: 600;
`;

const Body = styled.p<{ $dark: boolean }>`
  margin: 0 0 22px;
  font-size: 0.92rem;
  line-height: 1.5;
  color: ${p => (p.$dark ? '#b6b6b6' : '#555')};
`;

const Row = styled.div`
  display: flex;
  gap: 10px;
`;

const Btn = styled.button<{ $variant: 'primary' | 'ghost'; $color: string; $dark: boolean }>`
  flex: 1;
  padding: 11px 14px;
  border-radius: 10px;
  font-size: 0.92rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${p => (p.$variant === 'primary' ? p.$color : p.$dark ? '#444' : '#ddd')};
  background: ${p => (p.$variant === 'primary' ? p.$color : 'transparent')};
  color: ${p => (p.$variant === 'primary' ? '#fff' : p.$dark ? '#cfcfcf' : '#444')};
  transition: filter 0.15s ease, background 0.15s ease;
  &:hover {
    filter: brightness(0.96);
  }
`;

interface Props {
  open: boolean;
  dark: boolean;
  accentColor: string;
  onAllow: () => void;
  onDismiss: () => void;
}

const LocationPromptModal: React.FC<Props> = ({ open, dark, accentColor, onAllow, onDismiss }) => {
  if (!open) return null;
  return (
    <Overlay onClick={onDismiss}>
      <Card $dark={dark} onClick={e => e.stopPropagation()}>
        <IconCircle $color={accentColor}>
          <MyLocationIcon />
        </IconCircle>
        <Title>Respostas mais precisas pra sua região?</Title>
        <Body $dark={dark}>
          Com a sua localização, o assistente responde melhor sobre o que está perto de você —
          clima, lugares, serviços. Dá pra desligar quando quiser no menu “+”.
        </Body>
        <Row>
          <Btn $variant="ghost" $color={accentColor} $dark={dark} onClick={onDismiss}>
            Agora não
          </Btn>
          <Btn $variant="primary" $color={accentColor} $dark={dark} onClick={onAllow}>
            Permitir
          </Btn>
        </Row>
      </Card>
    </Overlay>
  );
};

export default LocationPromptModal;
